// src/components/contracts/ContractTemplatesTab.jsx
// Manage the catalogue of contract templates. Each one CARRIES the legal
// document itself as HTML with {{placeholder}} merge fields; MSP OS renders it
// to PDF and DocuSign only runs the signing ceremony. There is nothing to
// upload or tag in DocuSign, and no DocuSign-side precondition for going
// active — content is the only requirement.
import { useState, useEffect, useMemo } from "react";
import {
  makeContractTemplate, makeFieldDef, FIELD_TYPES, DEFAULT_SOW_FIELDS,
  contractsForProposal,
} from "./contractModel";
import { getContractsConfig, getSeedContractContent, previewContract } from "../../api/contractsApi";

const card = { background: "var(--card,#fff)", border: "1px solid var(--border,#e2e2e2)", borderRadius: 12, padding: 16 };
const btn = (primary) => ({
  padding: "7px 13px", borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: "pointer",
  border: primary ? "none" : "1px solid var(--border,#d0d0d0)",
  background: primary ? "var(--dt-secondary,#0b80a5)" : "var(--card,#fff)",
  color: primary ? "#fff" : "var(--text,#222)",
});
const inp = {
  width: "100%", padding: "7px 9px", borderRadius: 7, fontSize: 12,
  border: "1px solid var(--border,#d0d0d0)", background: "var(--card,#fff)",
  color: "var(--text,#222)", fontFamily: "inherit",
};
const lbl = { display: "block", fontSize: 9, fontWeight: 700, letterSpacing: "0.06em",
  textTransform: "uppercase", color: "var(--text3,#888)", marginBottom: 3 };

// Which proposal tiers a contract can be attached to. Leads are excluded —
// a pre-proposal is not a thing anyone signs a contract against.
const TIER_KEYS = [
  { key: "bronze-sentinel", label: "Bronze Sentinel" },
  { key: "silver-guardian", label: "Silver Guardian" },
  { key: "gold-fortress", label: "Gold Fortress" },
  { key: "copper-defender", label: "Copper Defender" },
];

export default function ContractTemplatesTab({
  contractTemplates = [], setContractTemplates, contractInstances = [], saveSnapshot,
}) {
  const [openId, setOpenId] = useState(null);
  const [toast, setToast] = useState(null);
  const [dsConfig, setDsConfig] = useState(null);

  useEffect(() => {
    getContractsConfig().then(setDsConfig).catch(() => setDsConfig({ configured: false }));
  }, []);

  const flash = (text, kind = "ok") => {
    setToast({ text, kind });
    setTimeout(() => setToast(null), 4000);
  };

  // One updater, never two sequential commits. Two setX calls computed from the
  // same closure silently discard the first — the pattern documented in
  // CLAUDE.md after the ProposalsTab company-picker bug.
  const commit = (updater) => setContractTemplates((prev) => {
    const next = typeof updater === "function" ? updater(prev) : updater;
    saveSnapshot({ contractTemplates: next });
    return next;
  });

  const patch = (id, p) => commit((prev) => prev.map((t) =>
    (t.id === id ? { ...t, ...p, updatedAt: Date.now() } : t)));

  const addTemplate = () => {
    const t = makeContractTemplate({ name: "New contract", fieldSchema: DEFAULT_SOW_FIELDS() });
    commit((prev) => [...prev, t]);
    setOpenId(t.id);
  };

  const removeTemplate = (t) => {
    const sent = contractInstances.filter((c) => c.contractTemplateId === t.id && c.docuSignEnvelopeId).length;
    const msg = sent
      ? `"${t.name}" has ${sent} sent contract${sent === 1 ? "" : "s"} referencing it.\n\nDeleting the template does NOT recall those envelopes — they stay in DocuSign and keep their status. The sent records keep the name they were sent under.\n\nDelete the template?`
      : `Delete "${t.name}"?`;
    if (!window.confirm(msg)) return;
    commit((prev) => prev.filter((x) => x.id !== t.id));
    if (openId === t.id) setOpenId(null);
  };

  const open = useMemo(() => contractTemplates.find((t) => t.id === openId) || null,
    [contractTemplates, openId]);

  // The seeded legal text is FETCHED, not bundled — ~27KB per tier does not
  // belong in the frontend, and a second copy of a contract is how one gets
  // edited and the other gets sent.
  const loadSeed = (t) => {
    const tier = (t.appliesToTiers || [])[0];
    getSeedContractContent()
      .then((seeds) => {
        const hit = seeds.find((x) => x.tierKey === tier) || seeds[0];
        if (!hit) return flash("No seeded content available.", "err");
        if (String(t.content || "").trim()
            && !window.confirm("Replace the current content with the seeded legal text?")) return;
        patch(t.id, { content: hit.content, documentType: hit.documentType });
        flash(`Loaded the ${hit.name} text.`);
      })
      .catch((e) => flash(e?.response?.data?.error || e.message || "Could not load seed content", "err"));
  };

  // Renders the real PDF through DocRaptor with a sample client name, so the
  // document can be read in full before it is ever sent to anyone.
  const previewPdf = (t) => {
    if (!String(t.content || "").trim()) return flash("Nothing to preview yet.", "err");
    flash("Rendering preview…");
    previewContract({
      templateName: t.name, content: t.content,
      fieldValues: { clientCompany: "[Sample Client Pty Ltd]" },
      customerName: "[Sample Client Pty Ltd]",
    })
      .then((url) => { window.open(url, "_blank", "noopener"); flash("Preview opened in a new tab."); })
      .catch((e) => flash(e?.response?.data?.error || e.message || "Preview failed", "err"));
  };

  return (
    <div className="panel" style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "auto", padding: 20, gap: 16, width: "100%", minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Contract Templates</h2>
        <button style={{ ...btn(true), marginLeft: "auto" }} onClick={addTemplate}>+ New Contract Template</button>
      </div>

      {toast && (
        <div style={{ padding: "8px 12px", borderRadius: 8, fontSize: 12, fontWeight: 600,
          background: toast.kind === "err" ? "#fdeeee" : "#eefaef",
          color: toast.kind === "err" ? "#c0392b" : "#1e7d34" }}>{toast.text}</div>
      )}

      {/* v2: MSP OS renders the contract; DocuSign only runs the signing
          ceremony. This block used to instruct the reader to paste a DocuSign
          Template ID "below" — a field the v2 pivot deleted — so it described
          a setup step that could not be performed. */}
      <div style={{ ...card, fontSize: 12, color: "var(--text3,#777)", lineHeight: 1.6 }}>
        MSP OS <strong>renders these contracts itself</strong> and DocuSign only runs the signing
        ceremony — there is nothing to upload or tag in DocuSign. Load the seeded legal text into a
        template, read the rendered preview, then make it <em>active</em>. Having content is the only
        requirement to go active. Signature placement uses anchor text baked into the rendered PDF, so
        changing the wording never means reconfiguring DocuSign.
        {dsConfig && !dsConfig.configured && (
          <div style={{ marginTop: 8, padding: "7px 10px", borderRadius: 7, background: "#fff4e2", color: "#8a5a12", fontWeight: 600 }}>
            ⚠ DocuSign is not configured yet — the four DOCUSIGN-* secrets are missing from Key Vault.
            Templates can be set up now; sending will not work until that is done.
          </div>
        )}
        {dsConfig?.configured && (
          <div style={{ marginTop: 8, fontSize: 11 }}>
            DocuSign connected · <strong>{dsConfig.environment}</strong> environment.
            {" "}If the first send fails with a consent error,{" "}
            <a href={dsConfig.consentUrl} target="_blank" rel="noopener noreferrer">grant consent once here</a>.
          </div>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 12 }}>
        {contractTemplates.map((t) => {
          const usable = t.status === "active" && String(t.content || "").trim();
          const tiers = Array.isArray(t.appliesToTiers) ? t.appliesToTiers : [];
          return (
            <div key={t.id} style={{ ...card, cursor: "pointer", borderColor: openId === t.id ? "var(--dt-secondary,#0b80a5)" : undefined }}
                 onClick={() => setOpenId(openId === t.id ? null : t.id)}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                <div style={{ fontWeight: 700, fontSize: 13, flex: 1 }}>{t.name || "(untitled)"}</div>
                <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 5,
                  background: usable ? "#eefaef" : "#f2f2f2", color: usable ? "#1e7d34" : "#777" }}>
                  {t.status === "active" ? "Active" : "Draft"}
                </span>
              </div>
              <div style={{ fontSize: 11, color: "var(--text3,#888)", marginTop: 5 }}>
                {tiers.length
                  ? tiers.map((k) => TIER_KEYS.find((x) => x.key === k)?.label || k).join(", ")
                  : "Ancillary — available on any tier"}
              </div>
              <div style={{ fontSize: 11, color: "var(--text3,#888)", marginTop: 3 }}>
                {(t.fieldSchema || []).length} field{(t.fieldSchema || []).length === 1 ? "" : "s"}
                {" · "}
                {String(t.content || "").trim()
                  ? <span style={{ color: "#1e7d34" }}>{Math.round(String(t.content).length / 1000)}KB of content</span>
                  : <span style={{ color: "#c0392b" }}>no content yet</span>}
              </div>
            </div>
          );
        })}
        {!contractTemplates.length && (
          <div style={{ ...card, fontSize: 12, color: "var(--text3,#888)" }}>
            No contract templates yet.
          </div>
        )}
      </div>

      {open && (
        <Editor
          t={open}
          onPatch={(p) => patch(open.id, p)}
          onDelete={() => removeTemplate(open)}
          onClose={() => setOpenId(null)}
          flash={flash}
          onLoadSeed={() => loadSeed(open)}
          onPreview={() => previewPdf(open)}
        />
      )}
    </div>
  );
}

function Editor({ t, onPatch, onDelete, onClose, flash, onLoadSeed, onPreview }) {
  const tiers = Array.isArray(t.appliesToTiers) ? t.appliesToTiers : [];
  const fields = Array.isArray(t.fieldSchema) ? t.fieldSchema : [];

  const toggleTier = (key) => {
    const next = tiers.includes(key) ? tiers.filter((k) => k !== key) : [...tiers, key];
    onPatch({ appliesToTiers: next });
  };

  const patchField = (i, p) => onPatch({ fieldSchema: fields.map((f, j) => (j === i ? { ...f, ...p } : f)) });
  const removeField = (i) => onPatch({ fieldSchema: fields.filter((_, j) => j !== i) });
  const addField = () => onPatch({ fieldSchema: [...fields, makeFieldDef()] });
  const moveField = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= fields.length) return;
    const next = [...fields];
    [next[i], next[j]] = [next[j], next[i]];
    onPatch({ fieldSchema: next });
  };

  /* Activation is gated, not merely discouraged: an active template with
     nothing to render is selectable when sending and produces an empty
     document, which reads as a broken app rather than an unfinished setup.
     ⚠ There is NO DocuSign-side precondition under v2. This check used to
     require a docuSignTemplateId, which the v2 pivot deleted from the model —
     so `t.docuSignTemplateId` was undefined on every template and the guard
     fired every single time, making "Make active" a no-op that could never
     succeed no matter what was filled in. Content is the only requirement. */
  const activate = () => {
    if (!String(t.content || "").trim()) {
      flash("This template has no document content yet — use “Load seeded legal text”, then make it active.", "err");
      return;
    }
    // Field keys still matter: they are the {{placeholder}} names substituted
    // into the contract, so a duplicate key silently overwrites a value.
    const dupes = fields.filter((f) => f.key).map((f) => f.key);
    if (new Set(dupes).size !== dupes.length) {
      flash("Two fields share the same key — the contract substitutes by key, so one would overwrite the other.", "err");
      return;
    }
    if (fields.some((f) => !f.key)) {
      flash("Every field needs a key — it must match a {{placeholder}} in the contract text.", "err");
      return;
    }
    onPatch({ status: "active" });
    flash("Template is active and can now be sent.");
  };

  return (
    <div style={{ ...card, display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ fontWeight: 700, fontSize: 14 }}>Edit contract template</div>
        <button style={{ ...btn(false), marginLeft: "auto" }} onClick={onClose}>Close</button>
      </div>

      <div>
        <label style={lbl}>Name</label>
        <input style={inp} value={t.name || ""} onChange={(e) => onPatch({ name: e.target.value })}
               placeholder="e.g. 3CX Phone System Agreement" />
      </div>

      <div>
        <label style={lbl}>Applies to</label>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {TIER_KEYS.map((x) => (
            <button key={x.key} style={{ ...btn(tiers.includes(x.key)), fontSize: 11, padding: "5px 10px" }}
                    onClick={() => toggleTier(x.key)}>{x.label}</button>
          ))}
        </div>
        <div style={{ fontSize: 10, color: "var(--text3,#999)", marginTop: 4 }}>
          {tiers.length
            ? "Shown first when sending from a proposal of a matching tier."
            : "None selected — this is an ancillary contract, offered on every proposal regardless of tier."}
        </div>
      </div>

      <div>
        <label style={lbl}>Document type</label>
        <select style={{ ...inp, maxWidth: 260 }} value={t.documentType || "managed-services"}
                onChange={(e) => onPatch({ documentType: e.target.value })}>
          <option value="managed-services">Managed Services</option>
          <option value="managed-devices">Managed Devices</option>
          <option value="copper-devices">Copper Defender Devices</option>
        </select>
        <div style={{ fontSize: 10, color: "var(--text3,#999)", marginTop: 3 }}>
          Bronze's contract is a Managed <em>Devices</em> SOW — a genuinely different document,
          not a repriced Managed Services one.
        </div>
      </div>

      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
          <label style={{ ...lbl, marginBottom: 0 }}>Contract content (HTML)</label>
          <button style={{ ...btn(false), marginLeft: "auto", fontSize: 10.5, padding: "3px 8px" }}
                  onClick={onLoadSeed}>Load seeded legal text</button>
          <button style={{ ...btn(false), fontSize: 10.5, padding: "3px 8px" }}
                  onClick={onPreview}>👁 Preview PDF</button>
        </div>
        <div style={{ fontSize: 10, color: "var(--text3,#999)", marginBottom: 5 }}>
          The legal document itself. <code>{"{{fieldKey}}"}</code> placeholders are replaced with the
          values filled in at send time. The signature and date blocks are appended automatically,
          with the DocuSign anchors inside them — do not write them here.
        </div>
        <textarea style={{ ...inp, fontFamily: "monospace", fontSize: 11, resize: "vertical" }}
                  rows={14} value={t.content || ""}
                  onChange={(e) => onPatch({ content: e.target.value })}
                  placeholder="Load the seeded legal text, then edit." />
      </div>

      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <label style={{ ...lbl, marginBottom: 0 }}>Fields to fill when sending</label>
          <button style={{ ...btn(false), marginLeft: "auto", fontSize: 11, padding: "4px 9px" }}
                  onClick={addField}>+ Add field</button>
        </div>
        <div style={{ fontSize: 10, color: "var(--text3,#999)", marginBottom: 8 }}>
          <strong>Key</strong> must equal the tab label set on the field in DocuSign — that is how the
          value finds its place in the document. <strong>Auto-fill from</strong> pre-populates from the
          proposal; it is only a starting point and stays editable when sending.
        </div>

        {fields.map((f, i) => (
          <div key={i} style={{ display: "flex", gap: 6, alignItems: "flex-end", marginBottom: 6,
                                padding: 8, border: "1px solid var(--border,#eee)", borderRadius: 8 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <label style={lbl}>Key (DocuSign tab label)</label>
              <input style={inp} value={f.key || ""} onChange={(e) => patchField(i, { key: e.target.value.trim() })} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <label style={lbl}>Label</label>
              <input style={inp} value={f.label || ""} onChange={(e) => patchField(i, { label: e.target.value })} />
            </div>
            <div style={{ width: 100 }}>
              <label style={lbl}>Type</label>
              <select style={inp} value={f.type || "text"} onChange={(e) => patchField(i, { type: e.target.value })}>
                {FIELD_TYPES.map((x) => <option key={x} value={x}>{x}</option>)}
              </select>
            </div>
            <div style={{ width: 130 }}>
              <label style={lbl}>Auto-fill from</label>
              <select style={inp} value={f.autoFillFrom || ""}
                      onChange={(e) => patchField(i, { autoFillFrom: e.target.value || null })}>
                <option value="">— manual —</option>
                <option value="customerName">customerName</option>
                <option value="customerFullName">customerFullName</option>
                <option value="customerContact">customerContact</option>
                <option value="customerEmail">customerEmail</option>
              </select>
            </div>
            <label style={{ fontSize: 10, display: "flex", alignItems: "center", gap: 4, paddingBottom: 8, whiteSpace: "nowrap" }}>
              <input type="checkbox" checked={!!f.required} onChange={(e) => patchField(i, { required: e.target.checked })} />
              req
            </label>
            <div style={{ display: "flex", flexDirection: "column", gap: 3, paddingBottom: 4 }}>
              <button style={{ ...btn(false), fontSize: 9, padding: "1px 6px" }} disabled={i === 0}
                      title="Move up" onClick={() => moveField(i, -1)}>↑</button>
              <button style={{ ...btn(false), fontSize: 9, padding: "1px 6px" }} disabled={i === fields.length - 1}
                      title="Move down" onClick={() => moveField(i, 1)}>↓</button>
            </div>
            <button style={{ ...btn(false), fontSize: 10, padding: "4px 8px", marginBottom: 4 }}
                    onClick={() => removeField(i)}>✕</button>
          </div>
        ))}
        {!fields.length && (
          <div style={{ fontSize: 11, color: "var(--text3,#999)" }}>
            No fields — this contract would send with nothing filled in.
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center", borderTop: "1px solid var(--border,#eee)", paddingTop: 10 }}>
        {t.status === "active"
          ? <button style={btn(false)} onClick={() => onPatch({ status: "draft" })}>Make draft</button>
          : <button style={btn(true)} onClick={activate}>Make active</button>}
        <span style={{ fontSize: 11, color: "var(--text3,#999)" }}>
          {t.status === "active"
            ? "Selectable when sending a contract from a won proposal."
            : "Draft templates are not offered when sending."}
        </span>
        <button style={{ ...btn(false), marginLeft: "auto", color: "#c0392b" }} onClick={onDelete}>Delete</button>
      </div>
    </div>
  );
}

// Re-exported so ProposalsTab can count a proposal's contracts without
// importing the model twice.
export { contractsForProposal };
